import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useAuth } from "@/App";

/* Brand tokens */
const NAVY = "#0B1D33";
const OFF_WHITE = "#F4F1EC";
const CREAM = "#F7F2E8";
const FAIRWAY = "#2E4834";
const FAIRWAY_ACCENT = "#3F6B4A";
const STONE = "#8A867E";
const SAND = "#D9C7A7";
const FONT_STACK = "'Hanken Grotesk', system-ui, -apple-system, sans-serif";
const FONT_SCRIPT = "'Caveat', 'Dancing Script', cursive";

const IMG_HERO_BG = "/cadii/backgrounds/welcome_bg.jpg";
const IMG_JOIN = "https://images.pexels.com/photos/34445201/pexels-photo-34445201.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const BIRD_WALKER = "/cadii/mascots/bird_walker_navy.png";
const BIRD_CADDIE = "/cadii/mascots/bird_caddie.png";
const LOCKUP_NAVY = "/cadii/brand/lockup_cadii_navy.png";

/* ─── Root ─── */
const LandingHome = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard");
  }, [user, loading, navigate]);

  const goLogin = () => navigate("/login");

  return (
    <div style={{ backgroundColor: CREAM, color: NAVY, fontFamily: FONT_STACK }}>
      <Helmet>
        <title>CADii · El golf vive aquí</title>
        <meta
          name="description"
          content="CADii es la app y plataforma de golf operada por Multigoods Inmuebles, S. de R.L. de C.V. Rondas en vivo, handicap, torneos y comunidad."
        />
        <meta property="og:site_name" content="CADii" />
        <meta property="og:title" content="CADii · El golf vive aquí" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.cadii.app" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Multigoods Inmuebles, S. de R.L. de C.V.",
          "legalName": "Multigoods Inmuebles, S. de R.L. de C.V.",
          "alternateName": "CADii",
          "brand": { "@type": "Brand", "name": "CADii" },
          "url": "https://www.cadii.app",
          "sameAs": ["https://www.cadii.app"],
        })}</script>
      </Helmet>
      <TopNav onLogin={goLogin} />
      <Hero onLogin={goLogin} />
      <FeaturesGrid />
      <CloserLook />
      <JoinSection onLogin={goLogin} />
      <SiteFooter />
    </div>
  );
};

/* ─── Nav ─── */
const TopNav = ({ onLogin }) => {
  const [active, setActive] = useState("inicio");
  const items = [
    { k: "inicio", label: "Inicio", to: "#top" },
    { k: "jugar", label: "Jugar", to: "#features" },
    { k: "comunidad", label: "Comunidad", to: "#closer" },
    { k: "torneos", label: "Torneos", to: "#closer" },
    { k: "clubes", label: "Clubes", to: "/cadiihouse" },
  ];
  const scrollTo = (target) => {
    if (target.startsWith("/")) {
      window.location.href = target;
      return;
    }
    if (target === "#top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const id = target.slice(1);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <nav
      className="sticky top-0 z-50"
      style={{
        backgroundColor: "rgba(247,242,232,0.9)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        borderBottom: `1px solid rgba(11,29,51,0.06)`,
      }}
    >
      <div className="max-w-[1360px] mx-auto flex items-center justify-between px-6 lg:px-10 py-4">
        <button onClick={() => scrollTo("#top")} className="flex-shrink-0 flex items-baseline gap-2.5" data-testid="home-nav-logo">
          <span style={{ fontFamily: "'Caveat', cursive", color: NAVY, fontSize: 34, fontWeight: 600, letterSpacing: "-1px", lineHeight: 1 }}>
            cadii
          </span>
          <span
            className="hidden sm:inline"
            style={{ color: NAVY, fontSize: 8.5, letterSpacing: "1.6px", fontWeight: 800, lineHeight: 1 }}
          >
            GOLF<br />GOLF<br />CLUB
          </span>
        </button>
        <div className="hidden md:flex items-center gap-9">
          {items.map((it) => (
            <button
              key={it.k}
              onClick={() => { setActive(it.k); scrollTo(it.to); }}
              className="relative py-2"
              style={{ color: NAVY, fontSize: 15, fontWeight: 600, letterSpacing: "-0.1px" }}
              data-testid={`home-nav-${it.k}`}
            >
              {it.label}
              {active === it.k && (
                <span
                  className="absolute left-1/2 -translate-x-1/2 -bottom-0.5"
                  style={{ width: 22, height: 2, backgroundColor: FAIRWAY_ACCENT, borderRadius: 2 }}
                />
              )}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4 sm:gap-6">
          <button className="p-2 hidden sm:block" style={{ color: NAVY }} aria-label="Buscar">
            <Search size={19} strokeWidth={2.2} />
          </button>
          <button
            onClick={onLogin}
            className="hidden sm:block"
            style={{ color: NAVY, fontSize: 15, fontWeight: 600 }}
            data-testid="home-nav-login"
          >
            Iniciar sesión
          </button>
          <button
            onClick={onLogin}
            className="rounded-full px-5 py-2.5 sm:px-6 sm:py-3"
            style={{
              backgroundColor: FAIRWAY_ACCENT, color: OFF_WHITE,
              fontSize: 14, fontWeight: 700,
            }}
            data-testid="home-nav-download"
          >
            Descargar app
          </button>
        </div>
      </div>
    </nav>
  );
};

/* ─── Hero ─── */
const Hero = ({ onLogin }) => (
  <section id="top" className="relative overflow-hidden" style={{ backgroundColor: CREAM }} data-testid="home-hero">
    <img
      src={IMG_HERO_BG}
      alt=""
      className="absolute inset-0 w-full h-full object-cover opacity-45"
      draggable={false}
    />
    <div
      className="absolute inset-0"
      style={{ backgroundImage: `linear-gradient(to bottom, rgba(247,242,232,0.85), rgba(247,242,232,0.7) 40%, rgba(247,242,232,0.95))` }}
    />

    <div className="relative z-10 max-w-[1360px] mx-auto px-6 lg:px-10 pt-10 lg:pt-14 pb-10 lg:pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] gap-8 lg:gap-12 items-start">
        {/* LEFT */}
        <div className="pt-4">
          <p className="text-[11px]" style={{ letterSpacing: "3.6px", color: STONE, fontWeight: 700 }}>
            GOLF&nbsp;&nbsp;/&nbsp;&nbsp;COMUNIDAD&nbsp;&nbsp;/&nbsp;&nbsp;CULTURA
          </p>
          <h1
            className="mt-6 text-[64px] sm:text-[92px] lg:text-[128px] leading-[0.9]"
            style={{ color: NAVY, fontWeight: 800, letterSpacing: "-3px" }}
            data-testid="home-hero-headline"
          >
            Play.<br />Capture.<br />Share.
          </h1>
          <p
            className="mt-8 max-w-[440px] text-[15px] sm:text-[16px]"
            style={{ color: NAVY, opacity: 0.75, lineHeight: 1.55, fontWeight: 400 }}
          >
            CADII es el hogar digital para golfistas, clubes y torneos. Juega más, lleva tu score, conecta con tu comunidad y forma parte de una cultura moderna de golf.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <StoreButton kind="apple" onClick={onLogin} testid="home-hero-appstore" />
            <StoreButton kind="google" onClick={onLogin} testid="home-hero-playstore" />
          </div>
          <p className="mt-10 text-[11px]" style={{ letterSpacing: "3.6px", color: STONE, fontWeight: 700 }}>
            MEJOR&nbsp;&nbsp;GOLF.&nbsp;&nbsp;MEJOR&nbsp;&nbsp;JUNTOS.
          </p>
        </div>

        {/* RIGHT — phones + mascot */}
        <div className="relative flex justify-center lg:justify-end items-start pt-4">
          <div className="relative w-full max-w-[560px]">
            {/* Handwritten */}
            <p
              className="absolute -left-4 -top-4 rotate-[-7deg] z-30 hidden sm:block pointer-events-none"
              style={{ fontFamily: FONT_SCRIPT, color: FAIRWAY, fontSize: 42, fontWeight: 600, lineHeight: 0.95, textShadow: "0 1px 0 rgba(247,242,232,0.7)" }}
            >
              Más golf<br />juntos.
            </p>
            {/* Phones */}
            <div className="relative flex justify-center items-start gap-3 sm:gap-4">
              <div className="mt-8 z-10">
                <PhoneMockA />
              </div>
              <div className="-mt-2 z-20">
                <PhoneMockB />
              </div>
            </div>
            {/* Bird — z-30 to sit above the phones */}
            <img
              src={BIRD_WALKER}
              alt=""
              className="absolute z-30 select-none pointer-events-none"
              style={{ left: "-6%", bottom: "-6%", width: 190, opacity: 0.95, filter: "drop-shadow(0 8px 16px rgba(11,29,51,0.18))" }}
              draggable={false}
            />
            {/* Side caption */}
            <p
              className="absolute -right-8 top-24 hidden lg:block"
              style={{
                color: STONE, fontSize: 10.5, letterSpacing: "2.6px",
                fontWeight: 700, writingMode: "vertical-rl", transform: "rotate(180deg)",
              }}
            >
              EL MISMO JUEGO. UN MAÑANA MÁS BRILLANTE.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const StoreButton = ({ kind, onClick, testid }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-3 rounded-2xl px-5 py-3.5 transition-transform active:scale-[0.98]"
    style={{ backgroundColor: NAVY, color: OFF_WHITE }}
    data-testid={testid}
  >
    <StoreIcon kind={kind} />
    <div className="text-left leading-tight">
      <p style={{ fontSize: 10, opacity: 0.85, fontWeight: 600 }}>
        {kind === "apple" ? "Descargar en" : "Disponible en"}
      </p>
      <p style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-0.3px" }}>
        {kind === "apple" ? "App Store" : "Google Play"}
      </p>
    </div>
  </button>
);
const StoreIcon = ({ kind }) => kind === "apple" ? (
  <svg width="22" height="24" viewBox="0 0 384 512" fill="currentColor" aria-hidden><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
) : (
  <svg width="22" height="24" viewBox="0 0 512 512" fill="currentColor" aria-hidden><path d="M325.3 234.3L104.4 31.1c-8.5-7.9-22.6-8-31.2-.3-6.3 5.7-9.2 14.3-7.1 22.8l91.8 158.9zM56.7 25.6c-1.5 3.9-2.2 8.2-2.2 12.9v434.9c0 4.7.7 9 2.2 12.9L306.9 256 56.7 25.6zM496.1 232.2l-93.5-51.3-104 96.1 104 96 93.9-51.5c14.1-9.5 21.5-24.4 21.5-40.3s-7.5-30.9-21.9-49zM72.3 481.4l253-232.6L177.8 299 65.7 480.5c8.6 8.4 22.4 8.7 31.5.9z"/></svg>
);

/* ─── Phones ─── */
const PhoneShell = ({ children }) => (
  <div
    className="relative rounded-[38px] overflow-hidden shadow-2xl"
    style={{
      width: 218, aspectRatio: "9 / 19.5",
      backgroundColor: OFF_WHITE, border: "8px solid #111",
    }}
  >
    <div className="absolute top-2 left-1/2 -translate-x-1/2 h-4 w-20 rounded-full z-30" style={{ backgroundColor: "#111" }} />
    {children}
  </div>
);

/* ─── Phone mock A: Home screen (mirrors /app/mobile/app/(tabs)/index.tsx) ─── */
const PhoneMockA = () => (
  <PhoneShell>
    <div className="h-full flex flex-col" style={{ backgroundColor: OFF_WHITE }}>
      {/* Status + header (empty | wordmark | bell) */}
      <div className="px-3 pt-8 pb-2 flex items-center justify-between">
        <span style={{ width: 20 }} />
        <img src={LOCKUP_NAVY} alt="" style={{ height: 12, width: "auto" }} />
        <div className="relative" style={{ width: 20, height: 20 }}>
          <div style={{
            width: 14, height: 14, marginLeft: 3, marginTop: 3,
            borderRadius: 3, border: `1.6px solid ${NAVY}`, position: "relative",
          }}>
            <span style={{ position: "absolute", top: -2, left: 5, width: 4, height: 3, borderRadius: 2, backgroundColor: NAVY }} />
          </div>
          <span style={{ position: "absolute", top: 1, right: 2, width: 5, height: 5, borderRadius: 3, backgroundColor: "#EF4444", border: `1px solid ${OFF_WHITE}` }} />
        </div>
      </div>

      {/* Greeting */}
      <div className="px-3 pt-1">
        <p style={{ fontSize: 15, color: NAVY, fontWeight: 800, letterSpacing: "-0.35px", lineHeight: 1.15 }}>
          Buenos días,<br />Alejandro.
        </p>
      </div>

      {/* RONDA ACTIVA */}
      <div className="px-3 mt-3">
        <p style={{ fontSize: 7.5, color: NAVY, fontWeight: 800, letterSpacing: "1.4px", opacity: 0.55 }}>RONDA ACTIVA</p>
        <div className="mt-1.5 rounded-2xl px-3 py-2.5" style={{ backgroundColor: FAIRWAY, boxShadow: "0 6px 16px rgba(46,72,52,0.28)" }}>
          {/* Course row */}
          <div className="flex items-center gap-1.5 mb-1.5">
            <span style={{ fontSize: 8 }}>🚩</span>
            <p style={{ fontSize: 8, color: OFF_WHITE, fontWeight: 700, letterSpacing: "0.4px" }}>Guadalajara CC</p>
          </div>
          {/* Body row */}
          <div className="flex items-center justify-between">
            <div>
              <p style={{ fontSize: 6.5, color: OFF_WHITE, opacity: 0.7, fontWeight: 800, letterSpacing: "0.8px" }}>HOYO</p>
              <p style={{ fontSize: 26, color: OFF_WHITE, fontWeight: 900, letterSpacing: "-1px", lineHeight: 1 }}>12</p>
              <p style={{ fontSize: 7, color: OFF_WHITE, opacity: 0.75, fontWeight: 700, marginTop: 1 }}>PAR 4 · 402 YDS</p>
            </div>
            {/* Score badge */}
            <div className="relative flex flex-col items-center justify-center"
                 style={{ width: 46, height: 46, borderRadius: 23, backgroundColor: OFF_WHITE, boxShadow: "inset 0 0 0 2px rgba(46,72,52,0.15)" }}>
              <p style={{ fontSize: 16, color: FAIRWAY, fontWeight: 900, letterSpacing: "-0.5px", lineHeight: 1 }}>-2</p>
              <p style={{ fontSize: 5.5, color: NAVY, fontWeight: 800, letterSpacing: "0.6px" }}>HOY</p>
            </div>
          </div>
          {/* CTA pill */}
          <div className="mt-2 rounded-full flex items-center justify-center gap-1.5 py-1.5"
               style={{ backgroundColor: OFF_WHITE }}>
            <span style={{ fontSize: 6.5, color: NAVY }}>▶</span>
            <span style={{ fontSize: 7.5, color: NAVY, fontWeight: 900, letterSpacing: "0.8px" }}>CONTINUAR RONDA</span>
          </div>
        </div>
      </div>

      {/* Rondas recientes */}
      <div className="px-3 mt-3">
        <div className="flex items-center justify-between mb-1">
          <p style={{ fontSize: 7.5, color: NAVY, fontWeight: 800, letterSpacing: "1.4px", opacity: 0.55 }}>RONDAS RECIENTES</p>
          <p style={{ fontSize: 6.5, color: FAIRWAY, fontWeight: 700 }}>Ver todas</p>
        </div>
        {[
          { c: "Bosques", d: "Ayer", s: "-1", g: "71" },
          { c: "Torrey Pines", d: "Sáb", s: "+2", g: "74" },
        ].map((r) => (
          <div key={r.c} className="rounded-lg px-2 py-1.5 flex items-center justify-between mb-1"
               style={{ backgroundColor: OFF_WHITE, border: "1px solid rgba(11,29,51,0.08)" }}>
            <div>
              <p style={{ fontSize: 9, color: NAVY, fontWeight: 800 }}>{r.c}</p>
              <p style={{ fontSize: 6.5, color: STONE, fontWeight: 600 }}>{r.d}</p>
            </div>
            <div className="text-right">
              <p style={{ fontSize: 10, fontWeight: 900, color: r.s.startsWith("-") ? FAIRWAY : NAVY }}>{r.s}</p>
              <p style={{ fontSize: 6.5, color: STONE, fontWeight: 700 }}>{r.g} golpes</p>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom tab bar */}
      <div className="mt-auto pb-2 pt-1 flex items-center justify-around border-t" style={{ borderColor: "rgba(11,29,51,0.06)" }}>
        {["Inicio", "Jugar", "Score", "Comunidad", "Más"].map((t, i) => (
          <span key={t} style={{ fontSize: 6.5, color: i === 0 ? NAVY : STONE, fontWeight: 700 }}>{t}</span>
        ))}
      </div>
    </div>
  </PhoneShell>
);

/* ─── Phone mock B: GCC Traditional score capture (mirrors /app/mobile/app/play/[id].tsx) ─── */
const PhoneMockB = () => (
  <PhoneShell>
    <div className="h-full flex flex-col" style={{ backgroundColor: OFF_WHITE }}>
      {/* Top bar */}
      <div className="px-2 pt-8 pb-1.5 flex items-center justify-between border-b" style={{ borderColor: "rgba(11,29,51,0.08)" }}>
        <span style={{ fontSize: 12, color: NAVY, fontWeight: 900, lineHeight: 1 }}>‹</span>
        <p style={{ fontSize: 7, color: NAVY, fontWeight: 900, letterSpacing: "1.2px" }}>GCC TRADICIONAL</p>
        <span style={{ fontSize: 8, color: NAVY, fontWeight: 800, backgroundColor: SAND, borderRadius: 8, padding: "1px 5px" }}>x2</span>
      </div>

      {/* Hole strip */}
      <div className="px-2 pt-1.5 pb-1 flex items-center gap-0.5 overflow-hidden">
        {Array.from({ length: 18 }).map((_, i) => {
          const done = i < 13;
          const active = i === 13;
          return (
            <div key={i} className="flex items-center justify-center flex-shrink-0"
                 style={{
                   width: 12.5, height: 12.5, borderRadius: 12.5,
                   backgroundColor: active ? NAVY : done ? "rgba(46,72,52,0.15)" : "rgba(11,29,51,0.06)",
                   fontSize: 6.5,
                   color: active ? OFF_WHITE : done ? FAIRWAY : NAVY,
                   fontWeight: 800,
                 }}>
              {done ? "✓" : i + 1}
            </div>
          );
        })}
      </div>

      {/* Hero HOYO */}
      <div className="px-3 py-1.5 flex items-end justify-between border-b" style={{ borderColor: "rgba(11,29,51,0.08)" }}>
        <div>
          <p style={{ fontSize: 7, color: NAVY, opacity: 0.5, fontWeight: 800, letterSpacing: "1.2px" }}>HOYO</p>
          <p style={{ fontSize: 30, color: NAVY, fontWeight: 900, letterSpacing: "-1.2px", lineHeight: 0.9 }}>14</p>
        </div>
        <div className="text-right">
          <p style={{ fontSize: 8, color: NAVY, fontWeight: 900, letterSpacing: "0.4px" }}>PAR 4</p>
          <p style={{ fontSize: 7, color: NAVY, opacity: 0.65, fontWeight: 800, letterSpacing: "0.4px" }}>420 YDS</p>
          <p style={{ fontSize: 6, color: NAVY, opacity: 0.4, fontWeight: 700 }}>HCP 5</p>
        </div>
      </div>

      {/* Column header row */}
      <div className="px-2 pt-1.5 pb-1 flex items-center" style={{ fontSize: 5.5, color: NAVY, opacity: 0.55, fontWeight: 800, letterSpacing: "1px" }}>
        <span style={{ flex: 1 }}>JUGADORES</span>
        <span style={{ width: 22, textAlign: "center" }}>GROSS</span>
        <span style={{ width: 22, textAlign: "center" }}>A PAR</span>
        <span style={{ width: 44, textAlign: "center" }}>HOYO</span>
      </div>

      {/* Player rows */}
      <div className="px-2 flex flex-col gap-1">
        {[
          { i: "AL", n: "Alejandro G.", gross: 4, ap: 0, strokes: 1, hole: 4 },
          { i: "RO", n: "Rodrigo M.", gross: 3, ap: -1, strokes: 0, hole: 3 },
          { i: "FE", n: "Fernando A.", gross: 5, ap: 1, strokes: 2, hole: 5 },
        ].map((p) => {
          const apColor = p.ap < 0 ? FAIRWAY : p.ap > 0 ? "#B00020" : NAVY;
          return (
            <div key={p.n} className="rounded-lg px-1.5 py-1 flex items-center"
                 style={{ backgroundColor: OFF_WHITE, border: "1px solid rgba(11,29,51,0.08)", boxShadow: "0 1px 2px rgba(11,29,51,0.04)" }}>
              {/* Avatar */}
              <div className="flex items-center justify-center flex-shrink-0"
                   style={{ width: 20, height: 20, borderRadius: 10, backgroundColor: NAVY }}>
                <span style={{ fontSize: 6.5, color: OFF_WHITE, fontWeight: 900, letterSpacing: "0.3px" }}>{p.i}</span>
              </div>
              {/* Name */}
              <div style={{ flex: 1, marginLeft: 5, minWidth: 0 }}>
                <p style={{ fontSize: 7.5, color: NAVY, fontWeight: 900, letterSpacing: "-0.1px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.n}</p>
                {p.strokes > 0 && (
                  <p style={{ fontSize: 5.5, color: NAVY, opacity: 0.5, fontWeight: 700 }}>Stroke ×{p.strokes}</p>
                )}
              </div>
              {/* Gross + stroke dots */}
              <div style={{ width: 22, textAlign: "center" }}>
                <p style={{ fontSize: 11, color: NAVY, fontWeight: 900, lineHeight: 1 }}>{p.gross}</p>
                {p.strokes > 0 && (
                  <div className="flex items-center justify-center gap-0.5 mt-0.5">
                    {Array.from({ length: p.strokes }).map((_, i) => (
                      <span key={i} style={{ width: 3.5, height: 3.5, borderRadius: 2, backgroundColor: FAIRWAY, display: "inline-block" }} />
                    ))}
                  </div>
                )}
              </div>
              {/* A par */}
              <div style={{ width: 22, textAlign: "center" }}>
                <p style={{ fontSize: 9, fontWeight: 900, color: apColor, lineHeight: 1 }}>
                  {p.ap === 0 ? "E" : (p.ap > 0 ? "+" + p.ap : p.ap)}
                </p>
              </div>
              {/* Stepper */}
              <div className="flex items-center justify-between"
                   style={{ width: 44, height: 18, borderRadius: 9, border: `1px solid rgba(11,29,51,0.15)`, padding: "0 3px", marginLeft: 2 }}>
                <span style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: "rgba(11,29,51,0.06)", color: NAVY, fontSize: 8, fontWeight: 900, textAlign: "center", lineHeight: "10px" }}>−</span>
                <span style={{ fontSize: 8, color: NAVY, fontWeight: 900 }}>{p.hole}</span>
                <span style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: NAVY, color: OFF_WHITE, fontSize: 8, fontWeight: 900, textAlign: "center", lineHeight: "10px" }}>+</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Save CTA */}
      <div className="mx-2 mt-1.5 rounded-full text-center py-1.5" style={{ backgroundColor: FAIRWAY }}>
        <span style={{ fontSize: 7.5, color: OFF_WHITE, fontWeight: 900, letterSpacing: "0.8px" }}>GUARDAR HOYO 14</span>
      </div>

      {/* Bottom tab bar */}
      <div className="mt-auto pb-2 pt-1 flex items-center justify-around border-t" style={{ borderColor: "rgba(11,29,51,0.06)" }}>
        {["Inicio", "Jugar", "Score", "Comunidad", "Más"].map((t, i) => (
          <span key={t} style={{ fontSize: 6.5, color: i === 2 ? NAVY : STONE, fontWeight: 700 }}>{t}</span>
        ))}
      </div>
    </div>
  </PhoneShell>
);

/* ─── Features grid ─── */
const FEATURES = [
  { icon: "🚩", title: "Score en Vivo", desc: "Lleva scoring en tiempo real para ti y tu grupo." },
  { icon: "💰", title: "Juegos y Apuestas", desc: "Agrégale más emoción y diversión a tu ronda." },
  { icon: "🏆", title: "Torneos", desc: "Únete, compite y da seguimiento a tu progreso." },
  { icon: "👥", title: "Comunidad", desc: "Jugadores, clubes y amigos, todo en un solo lugar." },
  { icon: "📸", title: "Historias", desc: "Captura y comparte tus momentos de golf." },
  { icon: "🎒", title: "Herramientas para Clubes", desc: "Todo lo que tu club necesita para conectar con sus jugadores." },
];

const FeaturesGrid = () => (
  <section id="features" className="py-16 lg:py-24 px-6 lg:px-10" style={{ backgroundColor: CREAM }} data-testid="home-features">
    <div className="max-w-[1360px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <p style={{ fontSize: 11, letterSpacing: "3.4px", color: STONE, fontWeight: 700 }}>
          TODO EL GOLF, EN UN SOLO LUGAR
        </p>
        <p style={{ fontSize: 11, letterSpacing: "3.4px", color: STONE, fontWeight: 700 }}>
          SIMPLE DE USAR. MÁS PODEROSO JUNTOS.
        </p>
      </div>
      <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-6">
        {FEATURES.map((f) => (
          <div key={f.title} className="flex flex-col items-start">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
              style={{ backgroundColor: "rgba(11,29,51,0.05)", border: `1px solid rgba(11,29,51,0.1)` }}
            >
              <span style={{ fontSize: 24 }}>{f.icon}</span>
            </div>
            <p style={{ fontSize: 16, color: NAVY, fontWeight: 800, letterSpacing: "-0.3px" }}>{f.title}</p>
            <p style={{ fontSize: 12.5, color: NAVY, opacity: 0.62, marginTop: 6, lineHeight: 1.45 }}>{f.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ─── Míralo más de cerca ─── */
const CloserLook = () => (
  <section id="closer" className="py-14 lg:py-20 px-6 lg:px-10" style={{ backgroundColor: CREAM }} data-testid="home-closer">
    <div className="max-w-[1360px] mx-auto">
      <div className="flex items-center justify-between mb-8">
        <p style={{ fontSize: 11, letterSpacing: "3.4px", color: STONE, fontWeight: 700 }}>
          MÍRALO MÁS DE CERCA
        </p>
        <p style={{ fontSize: 11, letterSpacing: "3.4px", color: STONE, fontWeight: 700 }} className="hidden md:block">
          DISEÑO HERMOSO. UN JUEGO MÁS CONECTADO.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ScoreCardTile />
        <TournamentTile />
        <LeaderboardTile />
      </div>
    </div>
  </section>
);

const CardShell = ({ children }) => (
  <div
    className="rounded-3xl p-6"
    style={{ backgroundColor: OFF_WHITE, border: `1px solid rgba(11,29,51,0.07)` }}
  >
    {children}
  </div>
);

const ScoreCardTile = () => {
  const hole = [1, 2, 3, 4, 5, 6, 7, 8, "OUT"];
  const par = [4, 4, 3, 5, 4, 4, 3, 5, 36];
  const score = [4, 4, 3, 5, 5, 4, 3, 4, 35];
  return (
    <CardShell>
      <div className="flex items-center justify-between">
        <span style={{ fontSize: 10, letterSpacing: "2px", color: NAVY, fontWeight: 800 }}>TARJETA</span>
        <span style={{ fontSize: 10, color: FAIRWAY, fontWeight: 700 }}>Ver tarjeta completa ›</span>
      </div>
      <div className="mt-4 overflow-hidden rounded-xl" style={{ border: `1px solid rgba(11,29,51,0.08)` }}>
        <ScoreRow cells={["HOYO", ...hole]} isHead />
        <ScoreRow cells={["PAR", ...par]} />
        <ScoreRow cells={["SCORE", ...score]} withCircles />
      </div>
      <div className="mt-4 flex items-center gap-4">
        <Legend color={FAIRWAY_ACCENT} label="Bajo Par" />
        <Legend color={STONE} label="Par" />
        <Legend color="#E64545" label="Sobre Par" />
      </div>
    </CardShell>
  );
};

const ScoreRow = ({ cells, isHead, withCircles }) => (
  <div className="grid grid-cols-10 text-center" style={{ borderTop: isHead ? "none" : `1px solid rgba(11,29,51,0.06)` }}>
    {cells.map((v, i) => {
      const label = i === 0;
      const highlight = withCircles && !label ? (
        // fake bogey circle on hole 5 (index 5) and birdie ring on hole 8 (index 8)
        i === 5 ? "bogey" : i === 8 ? "birdie" : null
      ) : null;
      return (
        <div
          key={i}
          className="py-2 text-[11px]"
          style={{
            color: label ? STONE : NAVY,
            fontWeight: label ? 800 : isHead ? 800 : 700,
            letterSpacing: label ? "1px" : "0",
            borderLeft: i === 0 ? "none" : `1px solid rgba(11,29,51,0.05)`,
            backgroundColor: i === 9 && !label ? "rgba(11,29,51,0.03)" : "transparent",
          }}
        >
          {highlight ? (
            <span
              className="inline-flex items-center justify-center w-5 h-5 rounded-full"
              style={{
                border: `2px solid ${highlight === "bogey" ? "#E64545" : FAIRWAY_ACCENT}`,
                color: NAVY, fontWeight: 800, fontSize: 10,
              }}
            >
              {v}
            </span>
          ) : v}
        </div>
      );
    })}
  </div>
);

const Legend = ({ color, label }) => (
  <div className="flex items-center gap-1.5">
    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
    <span style={{ fontSize: 11, color: NAVY, fontWeight: 600 }}>{label}</span>
  </div>
);

const TournamentTile = () => (
  <CardShell>
    <div className="flex items-center justify-between">
      <span style={{ fontSize: 10, letterSpacing: "2px", color: NAVY, fontWeight: 800 }}>TORNEO</span>
      <span style={{ fontSize: 10, color: FAIRWAY, fontWeight: 700 }}>Ver todo ›</span>
    </div>
    <div
      className="mt-4 rounded-xl overflow-hidden h-32 relative"
      style={{
        backgroundImage: `url(${IMG_JOIN})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    />
    <div className="mt-4">
      <p style={{ fontSize: 16, color: NAVY, fontWeight: 800, letterSpacing: "-0.3px" }}>Cadii Invitational</p>
      <p style={{ fontSize: 12, color: NAVY, opacity: 0.65, marginTop: 2 }}>Torrey Pines Golf Club</p>
      <p style={{ fontSize: 11, color: STONE, marginTop: 6, fontWeight: 600 }}>12 – 14 de abr. de 2026</p>
    </div>
  </CardShell>
);

const LeaderboardTile = () => {
  const rows = [
    { pos: 1, name: "Fernando A.", today: "-3", total: "-8" },
    { pos: 2, name: "Alejandro G.", today: "-2", total: "-6" },
    { pos: 3, name: "Carlos M.", today: "E", total: "-2" },
    { pos: 4, name: "Juan P.", today: "+1", total: "+1" },
  ];
  return (
    <CardShell>
      <div className="flex items-center justify-between">
        <span style={{ fontSize: 10, letterSpacing: "2px", color: NAVY, fontWeight: 800 }}>POSICIONES</span>
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: FAIRWAY_ACCENT }} />
          <span style={{ fontSize: 10, color: FAIRWAY, fontWeight: 700 }}>En vivo</span>
        </div>
      </div>
      <div className="mt-4">
        <div className="grid grid-cols-[32px_1fr_50px_50px] text-[10px] py-2" style={{ color: STONE, fontWeight: 800, letterSpacing: "1px", borderBottom: `1px solid rgba(11,29,51,0.06)` }}>
          <span>POS</span><span>JUGADOR</span><span className="text-right">HOY</span><span className="text-right">TOTAL</span>
        </div>
        {rows.map((r) => (
          <div key={r.pos} className="grid grid-cols-[32px_1fr_50px_50px] py-3 items-center" style={{ borderBottom: `1px solid rgba(11,29,51,0.04)` }}>
            <span style={{ fontSize: 12, color: NAVY, fontWeight: 800 }}>{r.pos}</span>
            <span style={{ fontSize: 13, color: NAVY, fontWeight: 600 }}>{r.name}</span>
            <span style={{ fontSize: 13, color: NAVY, opacity: 0.7, textAlign: "right", fontWeight: 700 }}>{r.today}</span>
            <span style={{ fontSize: 13, color: NAVY, fontWeight: 800, textAlign: "right" }}>{r.total}</span>
          </div>
        ))}
      </div>
    </CardShell>
  );
};

/* ─── Join / Únete ─── */
const JoinSection = ({ onLogin }) => (
  <section className="px-6 lg:px-10 py-14 lg:py-20" style={{ backgroundColor: CREAM }} data-testid="home-join">
    <div className="max-w-[1360px] mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] gap-6 lg:gap-10 items-stretch">
      {/* Photo card */}
      <div className="relative rounded-3xl overflow-hidden" style={{ minHeight: 380 }}>
        <img src={IMG_JOIN} alt="" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(11,29,51,0) 30%, rgba(11,29,51,0.7))` }} />
        <div className="absolute bottom-0 left-0 p-8">
          <p
            style={{
              fontSize: 11, letterSpacing: "3.4px",
              color: OFF_WHITE, fontWeight: 700, lineHeight: 1.9,
            }}
          >
            PERSONAS<br />CAMPOS<br />HISTORIAS<br />UN MAÑANA DE GOLF<br />MÁS BRILLANTE.
          </p>
        </div>
      </div>

      {/* CTA card */}
      <div className="relative rounded-3xl p-8 lg:p-10 grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] gap-6 items-end" style={{ backgroundColor: OFF_WHITE, border: `1px solid rgba(11,29,51,0.07)`, minHeight: 380 }}>
        <div className="flex flex-col justify-between h-full">
          <div>
            <p style={{ fontSize: 11, letterSpacing: "3.4px", color: STONE, fontWeight: 700 }}>ÚNETE A CADII</p>
            <h3
              className="mt-4 text-[28px] sm:text-[36px] lg:text-[42px] leading-[1.02]"
              style={{ color: NAVY, fontWeight: 800, letterSpacing: "-1.2px" }}
            >
              Te espera una comunidad<br />de golf más conectada.
            </h3>
            <p className="mt-5 max-w-[440px] text-[14.5px]" style={{ color: NAVY, opacity: 0.72, lineHeight: 1.55 }}>
              Descarga la app, únete a tu club y forma parte de lo que sigue en el golf.
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <StoreButton kind="apple" onClick={onLogin} testid="home-join-appstore" />
            <StoreButton kind="google" onClick={onLogin} testid="home-join-playstore" />
          </div>
        </div>
        <div className="hidden sm:flex flex-col items-center justify-end pl-2 pointer-events-none">
          <p
            className="text-center mb-1"
            style={{ fontFamily: FONT_SCRIPT, color: FAIRWAY, fontSize: 28, fontWeight: 600, lineHeight: 1 }}
          >
            Play<br />Capture<br />Share
          </p>
          <img src={BIRD_CADDIE} alt="" className="w-24 lg:w-28 opacity-90" draggable={false} />
        </div>
      </div>
    </div>
  </section>
);

/* ─── Footer ─── */
const SiteFooter = () => (
  <footer className="px-6 lg:px-10 py-10" style={{ backgroundColor: CREAM, borderTop: `1px solid rgba(11,29,51,0.08)` }} data-testid="site-footer">
    <div className="max-w-[1360px] mx-auto flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
      <div className="flex items-baseline gap-2.5">
        <span style={{ fontFamily: "'Caveat', cursive", color: NAVY, fontSize: 40, fontWeight: 600, letterSpacing: "-1px", lineHeight: 1 }}>
          cadii
        </span>
        <span style={{ color: NAVY, fontSize: 9, letterSpacing: "1.6px", fontWeight: 800, lineHeight: 1 }}>
          GOLF<br />GOLF<br />CLUB
        </span>
      </div>
      <div className="flex flex-wrap gap-6">
        {[
          ["Acerca de", "/marketing"],
          ["Ayuda", "/support"],
          ["Contacto", "/contact"],
          ["Privacy Policy", "/privacy"],
          ["Terms of Service", "/terms"],
        ].map(([l, to]) => (
          <Link key={l} to={to} style={{ color: NAVY, fontSize: 13, fontWeight: 600 }}>{l}</Link>
        ))}
      </div>
      <div className="flex items-center gap-6">
        <div className="flex gap-3" style={{ color: NAVY }}>
          <SocialCircle label="IG" />
          <SocialCircle label="TT" />
          <SocialCircle label="YT" />
        </div>
        <div className="pl-4" style={{ borderLeft: `1px solid rgba(11,29,51,0.15)` }}>
          <p style={{ fontSize: 11, color: NAVY, fontWeight: 800, letterSpacing: "2px" }}>CADII.APP</p>
          <p style={{ fontSize: 10, color: FAIRWAY, fontWeight: 700, letterSpacing: "1.6px", marginTop: 2 }}>
            EL GOLF VIVE AQUÍ.
          </p>
        </div>
      </div>
    </div>
    <div
      className="max-w-[1360px] mx-auto mt-8 pt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2"
      style={{ borderTop: `1px solid rgba(11,29,51,0.08)` }}
      data-testid="legal-entity-line"
    >
      <p style={{ color: NAVY, opacity: 0.75, fontSize: 11.5, fontWeight: 500, letterSpacing: 0.1 }}>
        CADii es operado por <strong style={{ fontWeight: 700 }}>Multigoods Inmuebles, S. de R.L. de C.V.</strong>
        <span className="hidden sm:inline"> · </span>
        <span className="block sm:inline">CADii is operated by Multigoods Inmuebles, S. de R.L. de C.V.</span>
      </p>
      <p style={{ color: NAVY, opacity: 0.55, fontSize: 11, fontWeight: 500 }}>
        © {new Date().getFullYear()} Multigoods Inmuebles, S. de R.L. de C.V.
      </p>
    </div>
  </footer>
);

const SocialCircle = ({ label }) => (
  <div
    className="w-8 h-8 rounded-full flex items-center justify-center"
    style={{ border: `1.4px solid ${NAVY}`, color: NAVY }}
  >
    <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.5px" }}>{label}</span>
  </div>
);

export default LandingHome;
